import React, {Component} from 'react';

import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import partition from 'lodash/partition';
import {$PropertyType} from 'utility-types';

import {DatagridContext} from '../../../../Datagrid/Datagrid.context';
import {
  ColumnHeaderProperty,
  DatagridProperty,
  DatagridSorting,
  DatagridSortingType,
} from '../../../../Datagrid/Datagrid.model';
import {
  DatagridTableViewColumn,
  DatagridTableViewPinnedColumns,
} from '../../../../Datagrid/ViewModes/Table/Column';
import {DatagridTableViewRow} from '../../../../Datagrid/ViewModes/Table/Row';
import {DatagridTableViewSorting} from '../../../../Datagrid/ViewModes/Table/Sorting';
import {Styles, styles} from './Head.style';

interface Props extends Styles {
  properties: ColumnHeaderProperty[];
  sorting: DatagridSorting;
  onSortingChange: (sorting: DatagridSorting) => void;
}

interface State {
  hovered: string;
}

const sortingHandler = (
  property: DatagridProperty,
  sorting: DatagridSorting,
  onSortingChange: $PropertyType<Props, 'onSortingChange'>,
) => () => {
  let newSorting: DatagridSorting;

  if (property.value === sorting.key) {
    newSorting = {
      key: property.value,
      type:
        sorting.type === DatagridSortingType.asc
          ? DatagridSortingType.desc
          : DatagridSortingType.asc,
    };
  } else {
    newSorting = {
      key: property.value,
      type: sorting.type,
    };
  }

  onSortingChange(newSorting);
};

const hoverHandler = (property: string, onHoverChange: (property: string) => void) => () =>
  onHoverChange(property);

class DatagridTableViewHead extends Component<Props, State> {
  state = {
    hovered: null,
  };

  setHovered = (property: string) => {
    this.setState({hovered: property});
  };

  renderColumn({
    index,
    printable,
    property,
  }: {
    index: number;
    printable: boolean;
    property: ColumnHeaderProperty;
  }) {
    const {classes, sorting, onSortingChange} = this.props;
    const {hovered} = this.state;

    const isSelected = property.value === sorting.key && !printable;
    const isHovered = property.value === hovered;
    const isClickable = typeof property.label === 'string' && property.value;

    return (
      <DatagridTableViewColumn
        style={property.style}
        classes={{
          root: property.columnRootClass,
          headRoot: classNames(
            {[classes.cursorInitial]: !isClickable},
            property.columnHeadRootClass,
          ),
          headText: property.columnHeadTextClass,
        }}
        key={`${property.value}${index}`}
        head
        highlighted={isSelected}
        onClick={isClickable ? sortingHandler(property, sorting, onSortingChange) : undefined}
        onMouseOver={hoverHandler(property.value, this.setHovered)}
        onMouseLeave={hoverHandler(null, this.setHovered)}
      >
        {property.label}
        {!printable && isClickable && (
          <DatagridTableViewSorting
            sorting={sorting.type}
            classes={{
              root: classNames({
                [classes.hideSortArrow]: !isSelected,
                [classes.smoothSortArrow]: !isSelected && isHovered,
              }),
            }}
          />
        )}
        {!!property.columnHeadExtra && (
          <div
            className={classNames(
              classes.extraContainerClass,
              property.columnHeadExtraContainerClass,
            )}
          >
            {property.columnHeadExtra}
          </div>
        )}
      </DatagridTableViewColumn>
    );
  }

  render() {
    const {classes} = this.props;
    const [pinnedProperties, properties] = partition(this.props.properties, ({pin}) => pin);

    return (
      <DatagridContext.Consumer>
        {({printable}) => {
          return (
            <div className={classes.root}>
              <DatagridTableViewRow classes={{root: classes.row}}>
                <DatagridTableViewPinnedColumns classes={{root: classes.pinnedColumns}}>
                  {pinnedProperties.map((property, index) => {
                    return this.renderColumn({index, printable, property});
                  })}
                </DatagridTableViewPinnedColumns>
                {properties.map((property, index) => {
                  return this.renderColumn({index, printable, property});
                })}
              </DatagridTableViewRow>
            </div>
          );
        }}
      </DatagridContext.Consumer>
    );
  }
}

export default withStyles(styles)(DatagridTableViewHead);
